import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowLeft, Download, Info, Upload } from 'lucide-react';
import useUIStore from '../../../store/useUIStore';

const CustomerImportPage = ({ sess = { uid: '', role: 'user' } }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const { showSuccess, showError } = useUIStore();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      showError('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('uid', sess.uid);

    try {
      setImporting(true);
      const response = await fetch('/chatbot/api/v1.0/import/customers', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        const imported = data.imported || 0;
        const failed = data.failed || 0;
        const baseMessage = data.message || 'Import completed';
        const detail = `Imported: ${imported}${failed ? ` | Failed: ${failed}` : ''}`;
        showSuccess(`${baseMessage}. ${detail}`);
        setResult(null);
      } else {
        setResult({
          success: false,
          message: data.message || 'Import failed',
        });
        showError(data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing customers:', error);
      setResult({
        success: false,
        message: 'Failed to import customers',
      });
      showError(error?.message || 'Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'name,phone,email,tags,notes\nJohn Doe,1234567890,john@example.com,customer,Sample note';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Typography variant="h5">Import Customers</Typography>
        <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/customers')}>
          Back
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Upload CSV File</Typography>

            <Alert icon={<Info size={18} />} severity="info">
              <Typography variant="body2">
                <strong>Instructions:</strong>
                <ul style={{ margin: '8px 0 0 16px' }}>
                  <li>Download the template CSV file</li>
                  <li>Fill in your customer data</li>
                  <li>Upload the completed file</li>
                  <li>Required fields: name, phone</li>
                </ul>
              </Typography>
            </Alert>

            <Button variant="outlined" startIcon={<Download size={16} />} onClick={downloadTemplate} sx={{ alignSelf: 'flex-start' }}>
              Download Template
            </Button>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Button variant="contained" component="label">
                  Select CSV File *
                  <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {file.name}
                  </Typography>
                )}

                {result && (
                  <Alert severity="error">
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">{result.message}</Typography>
                    </Stack>
                  </Alert>
                )}

                <Stack direction="row" spacing={2}>
                  <Button type="submit" variant="contained" startIcon={!importing ? <Upload size={16} /> : null} disabled={importing}>
                    {importing ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} color="inherit" />
                        <span>Importing...</span>
                      </Stack>
                    ) : (
                      'Import'
                    )}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/customers')}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerImportPage;
